import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { X } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

import { Composer } from './chat/Composer'
import { ConversationDrawer } from './chat/ConversationDrawer'
import { Transcript } from './chat/Transcript'
import type { useAgentChat } from './useAgentChat'
import type { useConversations } from './useConversations'

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`

const ErrorBar = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  flex-shrink: 0;
  border-top: 1px solid ${({ theme }) => theme.forecast.danger};
  background: ${({ theme }) => theme.forecast.redGlassBackground};
`

/**
 * The conversation: the past-conversations drawer, the transcript, and the
 * composer. The controls that open the drawer or start a new conversation sit
 * in the model bar above, so the visibility is the parent's to hold.
 *
 * A failure is shown here rather than appended to the transcript. It used to be
 * pushed in as an assistant message, which made a rate limit look like
 * something the agent said — and left it in the record forever.
 */
export const AgentChatPanel: React.FC<{
  siteId: string | null
  chat: ReturnType<typeof useAgentChat>
  conversations: ReturnType<typeof useConversations>
  showHistory: boolean
  onShowHistoryChange: (show: boolean) => void
}> = ({ siteId, chat, conversations, showHistory, onShowHistoryChange }) => {
  const { t } = useLingui()
  const starterPrompts = [
    t`Build a health model for the gas turbine compressor: isentropic efficiency, field-fitted against 2022.`,
  ]
  return (
    <Wrap>
      {showHistory && (
        <ConversationDrawer
          conversations={conversations}
          currentId={chat.conversationId}
          onOpen={(id) => {
            onShowHistoryChange(false)
            void chat.open(id)
          }}
        />
      )}
      <Transcript
        turns={chat.turns}
        sending={chat.sending}
        loading={chat.loading}
      />
      {chat.error && (
        <ErrorBar role='alert'>
          <Text variant='caption' color='destructive'>
            {chat.error}
          </Text>
          <Button
            variant='ghostMuted'
            size='icon-sm'
            aria-label={t`Dismiss`}
            onClick={chat.dismissError}>
            <X />
          </Button>
        </ErrorBar>
      )}
      <Composer
        disabled={!siteId}
        sending={chat.sending}
        onSend={(text) => void chat.send(text)}
        onStop={chat.stop}
        suggestions={
          chat.turns.length || chat.loading ? undefined : starterPrompts
        }
      />
    </Wrap>
  )
}
